import { useState } from 'react';

/**
 * Drag-to-reorder for a list, on the browser's own drag and drop.
 *
 * Only the handle starts a drag: pressing it arms its row, and only an armed row
 * is `draggable`. A row that is draggable as a whole cannot have its text
 * selected, and these rows hold ids people copy.
 *
 * The browser's drag does not work on touch screens, so `move` gives the same
 * result one step at a time for the up/down buttons.
 *
 * `onReorder` receives the full list of ids in the new order; the caller saves it.
 */
export function useSortable({ items, getId, onReorder, enabled = true }) {
  const [armedId, setArmedId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [overId, setOverId] = useState(null);

  const ids = items.map(getId);

  const reset = () => {
    setArmedId(null);
    setDraggingId(null);
    setOverId(null);
  };

  const place = (id, toIndex) => {
    const fromIndex = ids.indexOf(id);
    if (fromIndex < 0 || toIndex < 0 || toIndex >= ids.length || fromIndex === toIndex) return;

    const next = [...ids];
    next.splice(fromIndex, 1);
    next.splice(toIndex, 0, id);
    onReorder(next);
  };

  const move = (id, delta) => place(id, ids.indexOf(id) + delta);

  const handleProps = item => ({
    onMouseDown: () => enabled && setArmedId(getId(item)),
    onMouseUp: () => setArmedId(null)
  });

  const rowProps = item => {
    const id = getId(item);
    if (!enabled) return {};

    return {
      draggable: armedId === id,
      onDragStart: event => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(id));
        setDraggingId(id);
      },
      onDragOver: event => {
        if (draggingId === null) return;
        event.preventDefault();
        if (overId !== id) setOverId(id);
      },
      onDrop: event => {
        event.preventDefault();
        if (draggingId !== null) place(draggingId, ids.indexOf(id));
        reset();
      },
      onDragEnd: reset
    };
  };

  // Where the dragged row would land, so the row under the pointer can draw a
  // line on the side it will be inserted: above when moving up, below when down.
  const dropSide = item => {
    const id = getId(item);
    if (draggingId === null || overId !== id || draggingId === id) return null;
    return ids.indexOf(draggingId) > ids.indexOf(id) ? 'before' : 'after';
  };

  return {
    enabled,
    idOf: getId,
    rowProps,
    handleProps,
    move,
    dropSide,
    isDragging: item => draggingId === getId(item)
  };
}
